import React, { createRef, memo, useCallback,useEffect,useRef, useState } from "react";
import { Carousel } from "react-responsive-carousel";
import styled from "@emotion/styled";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import InfoIcon from "@mui/icons-material/Info";
import { Box, Tab, Tabs, Typography } from "@mui/material";

import { SubmitButton } from "../Button/SubmitButton";
import {
  RulesSection,
  WeekDaySection,
} from "../SideBar/CalendarSideBar/components/CreateScheduleMenu/components/Popup/components";
import { StudioWorkingTime } from "../StudioWorkingTime";

const CustomTabs = styled(Tabs)({
  border: "1px solid #D6D6DB",
  padding: "7px 16px",
  borderRadius: "8px",
  width: "656px",
  "& .MuiButtonBase-root": {
    fontSize: "16px",
    fontWeight: 500,
    color: "black",
    borderRadius: "6px",
  },
  "& .MuiTabs-flexContainer": {
    justifyContent: "space-between",
  },
  "& .Mui-selected": {
    color: "white !important",
    backgroundColor: "black",
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "transparent",
  },
  "& .MuiTab-root": {
    padding: 0,
    minHeight: "32px",
    minWidth: 0,
  },
});

const SubmitButtonOutlined = styled(SubmitButton)({
  border: "1px solid #BF295C",
  color: "#BF295C",
  backgroundColor: "#FAFAFA",
  "&:hover, &:active": {
    backgroundColor: "transparent",
  },
});

interface EmployeeChildProp {
  child: any;
  workTimesRef?: any;
  rulesRef?: any;
  onChangeRulesSelector: (newValue:any) => void;
  onChangeRules: (newValue:string) => void;
  onChange?: (newWeekdays: any) => void;
}

const EmployeeWorkingTimeSelectSection = ({
  child,
  workTimesRef,
  rulesRef,
  onChangeRulesSelector,
  onChangeRules,
  onChange,
}: EmployeeChildProp) => {
  const employeeSchedule = child?.masterSchedule?.workingTimes?.[0];
  const studioSchedule = child?.schedule?.workingTimes?.[0];

  return (
    <>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "20px",
          margin: "48px 0 24px 0",
        }}
      >
        {child.title}
      </Typography>
      <StudioWorkingTime
        studio={child}
        workingTime={studioSchedule}
        renderImage
      />
      <Box
        sx={{
          margin: "48px 0 24px",
        }}
      >
        <WeekDaySection
          ref={workTimesRef}
          isEmployeeCreation
          daysPeriods={studioSchedule?.daysPeriods}
          employeeDaysPeriods={employeeSchedule?.daysPeriods}
          onChange={onChange}
          label="Choose working hours"
        />
      </Box>

      <RulesSection
        period={employeeSchedule?.period || studioSchedule?.period}
        ref={rulesRef}
        onChangeRules={onChangeRules}
        onChangeRulesSelector={onChangeRulesSelector}
      />

      <Typography
        sx={{
          fontSize: "16px",
          fontWeight: "400",
          margin: "16px 0 12px",
          color: "#828282",
          display: "flex",
          alignItems: "center",
        }}
      >
        <InfoIcon
          sx={{
            width: "15.5px",
            height: "15.5px",
            color: "#B9B9B9",
            marginRight: "6px",
          }}
        />
        Set working hours according to studio’s schedule
      </Typography>
    </>
  );
};

interface EmployeeTabsWrapperProps {
  sx?: any;
  refs?: any;
  handleChangeDateSelector: (newValue:any) => void;
  handleChangeDate: (newValue:string) => void;
  onWeekdaysChange: (newWeekdays: any) => void;
  studiosSelected: any;
}

const EmployeeWorkingTimeCarousel = memo(({
  sx,
  refs,
  handleChangeDateSelector,
  handleChangeDate,
  onWeekdaysChange,
  studiosSelected,
}: EmployeeTabsWrapperProps) => {
  const [activeStep, setActiveStep] = useState(0);

  const handleChangeStep = (step: any) => {
    setActiveStep(step);
  };

  const handlePressNext = useCallback(() => {
    handleChangeStep(activeStep + 1);
  }, [activeStep]);

  const handlePressPrev = useCallback(() => {
    handleChangeStep(activeStep - 1);
  }, [activeStep]);

  useEffect(() => {
    if (activeStep >= studiosSelected.length) {
      setActiveStep(studiosSelected.length === 0 ? 0 : studiosSelected.length - 1)
    }
  }, [activeStep, studiosSelected]);

  return (
    <>
      <Box
        sx={{
          margin: "0 auto",
          ...sx,
        }}
      >
        {studiosSelected.length > 1 ? (
          <CustomTabs
            value={activeStep}
            centered
          >
            {studiosSelected.map((tab: any, index: number) => (
              <Tab
                key={tab.id}
                sx={{
                  width: `${100 / studiosSelected.length - 1}%`,
                }}
                label={tab.title}
                disableRipple
                onClick={() => handleChangeStep(index)}
              />
            ))}
          </CustomTabs>
        ) : null}
      </Box>
      <Box>
        <Carousel
          selectedItem={activeStep}
          showArrows={false}
          showStatus={false}
          showIndicators={false}
          showThumbs={false}
          transitionTime={0}
        >
          {studiosSelected.map((studio: any, index: any) => {
            const workingTimesRef = createRef();
            const rulesRef = createRef();

            // @ts-ignore
            refs.current[index] = {
              workingTimesRef,
              rulesRef,
            };

            return (
              <EmployeeWorkingTimeSelectSection
                onChange={onWeekdaysChange}
                onChangeRules={handleChangeDate}
                onChangeRulesSelector={handleChangeDateSelector}
                workTimesRef={workingTimesRef}
                rulesRef={rulesRef}
                key={studio.id}
                child={studio}
              />
            );
          })}
        </Carousel>
      </Box>
      {studiosSelected.length > 1 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            margin: "40px 0 80px",
          }}
        >
          <Box>
            {activeStep !== 0 || activeStep + 1 === studiosSelected.length ? (
              <SubmitButtonOutlined
                title="Back to previous studio"
                startIcon={<ChevronLeft />}
                onClick={handlePressPrev}
              />
            ) : null}
          </Box>
          <Box>
            {activeStep !== studiosSelected.length - 1 && (
              <SubmitButton
                endIcon={<ChevronRight />}
                title="Proceed to next studio"
                onClick={handlePressNext}
              />
            )}
          </Box>
        </Box>
      ) : null}
    </>
  );
});

export {EmployeeWorkingTimeCarousel}
